import * as React from 'react';
// MUI
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { grey } from '@mui/material/colors';

// Third Party
import { useQuery } from '@apollo/client';
import { Link } from "react-router-dom";

// Internal
import { GET_PUBLICATIONS, GET_TAGS } from '../../data/graphql/queries';
import { PublicationType } from '../../data/types';
import PublicationCard from '../../components/PublicationCard';
import en from '../../data/en.json';

const PAGE_SIZE = 20;

export default function PublicationsList() {
  const [searchInput, setSearchInput] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [offset, setOffset] = React.useState(0);
  const [allPublications, setAllPublications] = React.useState<PublicationType[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset when filters change
  React.useEffect(() => {
    setOffset(0);
    setAllPublications([]);
    setHasMore(true);
  }, [debouncedSearch, selectedTags]);

  const { data: tagsData } = useQuery(GET_TAGS, {
    variables: { featured: false },
  });

  const { loading, error, fetchMore } = useQuery(GET_PUBLICATIONS, {
    variables: {
      paging_offset: 0,
      paging_limit: PAGE_SIZE,
      search: debouncedSearch || undefined,
      tag_names: selectedTags.length > 0 ? selectedTags : undefined,
    },
    onCompleted: (data) => {
      setAllPublications(data.publications);
      setHasMore(data.publications.length === PAGE_SIZE);
      setOffset(PAGE_SIZE);
    },
  });

  // Infinite scroll via IntersectionObserver
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && hasMore) {
        fetchMore({
          variables: {
            paging_offset: offset,
            paging_limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
            tag_names: selectedTags.length > 0 ? selectedTags : undefined,
          },
        }).then(({ data }) => {
          const newItems: PublicationType[] = data.publications;
          setAllPublications((prev) => [...prev, ...newItems]);
          setHasMore(newItems.length === PAGE_SIZE);
          setOffset((prev) => prev + PAGE_SIZE);
        });
      }
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, hasMore, offset, debouncedSearch, selectedTags, fetchMore]);

  const toggleTag = (tagName: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]
    );
  };

  if (error) return <p>{en.common.error}: {error.message}</p>;

  return (
    <Grid container flexDirection="column" alignItems="center">
      <Box sx={{ width: 750, mb: 2, mt: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search publications..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {tagsData?.tags && tagsData.tags.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tagsData.tags.map((tag: { id: string; name: string }) => (
              <Chip
                key={tag.id}
                label={tag.name}
                onClick={() => toggleTag(tag.name)}
                color={selectedTags.includes(tag.name) ? 'primary' : 'default'}
                variant={selectedTags.includes(tag.name) ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>

      {allPublications.map((publication: PublicationType) => (
        <Link
          key={publication.uuid}
          to={`/publications/${publication.uuid}`}
          style={{ textDecoration: 'none', color: 'black' }}
        >
          <Box sx={{ m: 2, p: 2, border: 1, borderColor: grey[300], width: 750 }}>
            <PublicationCard publication={publication} showContent={true} showScrollbar={true} />
          </Box>
        </Link>
      ))}

      <div ref={sentinelRef} style={{ height: 1 }} />
      {loading && (
        <Box sx={{ my: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
    </Grid>
  );
}
